// Sons du bandit manchot (ui/slotMachine.js) : quatre instruments Tone.js de timbres bien différents, pour que
// chaque moment se distingue et dure assez pour s'entendre :
//   - la roue : un bruit rose filtré, qui couvre tout le tirage et devient de plus en plus grave (les rouleaux ralentissent) ;
//   - les « tic » : des impulsions carrées sèches, un cliquet qui ralentit pendant le tirage ;
//   - les « clac » : une percussion grave qui résonne (levier, arrêt de chaque rouleau, de plus en plus aigu) ;
//   - la cloche : une cloche FM qui sonne longtemps (la règle apparaît, bouton « Jouer »).
// Tout passe par `destination` : le nœud de volume du jeu (services/audio.js), donc le cran de volume s'applique.
// Chaque fonction prend en dernier argument `at`, l'instant de départ (secondes du contexte audio) ; par défaut,
// maintenant. Fonctions séparées de audio.js pour pouvoir les rendre hors ligne (Tone.Offline), par exemple en test.

const STOP_NOTES = ['E2', 'G2', 'C3']; // l'arrêt de chaque rouleau monte : mi, sol, do

/**
 * @param {typeof import('tone')} Tone
 * @param {import('tone').ToneAudioNode} destination
 */
export function createSlotSounds(Tone, destination) {
  // Roue : bruit rose passé dans un filtre passe-bande dont la fréquence descend pendant le tirage
  const whirrFilter = new Tone.Filter({ type: 'bandpass', frequency: 2600, Q: 0.9 }).connect(destination);
  const whirr = new Tone.NoiseSynth({
    noise: { type: 'pink' },
    envelope: { attack: 0.1, decay: 0.2, sustain: 0.8, release: 0.5 },
  }).connect(whirrFilter);
  whirr.volume.value = 9; // bien audible sur tout le tirage, sous le niveau des arrêts

  // « Tic » : impulsions carrées très courtes mais nettes
  const ticker = new Tone.PolySynth(Tone.Synth, {
    oscillator: { type: 'square' },
    envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.04 },
  }).connect(destination);
  ticker.volume.value = -6;

  // « Clac » : percussion grave qui résonne
  const thunk = new Tone.MembraneSynth({
    pitchDecay: 0.06,
    octaves: 4,
    oscillator: { type: 'sine' },
    envelope: { attack: 0.001, decay: 0.6, sustain: 0, release: 0.6 },
  }).connect(destination);
  thunk.volume.value = 4;

  // Cloche : FM, longue résonance
  const bell = new Tone.PolySynth(Tone.FMSynth, {
    harmonicity: 3.01,
    modulationIndex: 7,
    oscillator: { type: 'sine' },
    envelope: { attack: 0.001, decay: 2.2, sustain: 0, release: 1.8 },
    modulation: { type: 'square' },
    modulationEnvelope: { attack: 0.002, decay: 0.5, sustain: 0, release: 0.5 },
  }).connect(destination);
  bell.volume.value = 6; // la sonnerie de la règle doit dominer la fin du tirage et résonner longtemps

  return {
    /** Levier : deux coups graves, « ka-chunk ». */
    lever(at = Tone.now()) {
      thunk.triggerAttackRelease('G1', 0.4, at, 1);
      thunk.triggerAttackRelease('C2', 0.4, at + 0.13, 1);
    },

    /**
     * Roue et « tic » des rouleaux.
     * @param {number[]} timesMs instants des « tic » en ms depuis `at` (core/slot.js#spinTickTimes) ; ceux d'avant `at` sont ignorés
     * @param {number} durationMs durée de la roue, jusqu'à l'arrêt du dernier rouleau
     */
    ticks(timesMs, durationMs = 0, at = Tone.now()) {
      for (const ms of timesMs) {
        if (ms >= 0) ticker.triggerAttackRelease('A5', 0.05, at + ms / 1000, 0.8);
      }
      if (durationMs > 0) {
        const seconds = durationMs / 1000;
        whirrFilter.frequency.cancelScheduledValues(at);
        whirrFilter.frequency.setValueAtTime(2600, at);
        whirrFilter.frequency.exponentialRampToValueAtTime(240, at + seconds);
        whirr.triggerAttackRelease(seconds, at, 0.9);
      }
    },

    /** Arrêt du rouleau `index` (0, 1 ou 2) : un « clac » grave qui résonne, plus aigu à chaque rouleau, et un claquement sec. */
    stop(index, at = Tone.now()) {
      thunk.triggerAttackRelease(STOP_NOTES[index] ?? 'C3', 0.5, at, 1);
      ticker.triggerAttackRelease('A6', 0.05, at, 1);
    },

    /** La règle apparaît : arpège de cloche, do-mi-sol-do, qui résonne plus de deux secondes. */
    reveal(at = Tone.now()) {
      for (const [note, offset] of [['C5', 0], ['E5', 0.12], ['G5', 0.24], ['C6', 0.38]]) {
        bell.triggerAttackRelease(note, 1.8, at + offset, 0.8);
      }
    },

    /**
     * Un point du triple qui s'ajoute au score (ui/scoreboard.js#celebrateScore) : un « bling » de pièce, deux notes carrées
     * aiguës très rapprochées, si puis mi.
     */
    coin(at = Tone.now()) {
      ticker.triggerAttackRelease('B5', 0.04, at, 0.7);
      ticker.triggerAttackRelease('E6', 0.06, at + 0.035, 0.7);
    },

    /** « Jouer » : un coup grave et deux notes de cloche, la partie démarre. */
    start(at = Tone.now()) {
      thunk.triggerAttackRelease('C2', 0.4, at, 0.9);
      bell.triggerAttackRelease('G5', 1.2, at + 0.04, 0.75);
      bell.triggerAttackRelease('C6', 1.2, at + 0.16, 0.75);
    },
  };
}
